import React, { useRef, useCallback, useEffect } from 'react';

/**
 * Éditeur WYSIWYG simple basé sur contentEditable
 * Compatible avec toutes les versions de React, préserve le HTML pour WordPress
 */
const SimpleEditor = ({ value, onChange, className = '', placeholder = '' }) => {
  const editorRef = useRef(null);
  const lastValueRef = useRef('');
  const isInitializedRef = useRef(false);

  // Synchroniser le contenu initial et les mises à jour externes
  useEffect(() => {
    if (editorRef.current) {
      // Initialisation au premier montage
      if (!isInitializedRef.current && value) {
        editorRef.current.innerHTML = value;
        lastValueRef.current = value;
        isInitializedRef.current = true;
      }
      // Mise à jour si la valeur change de l'extérieur (pas par l'utilisateur)
      else if (value !== lastValueRef.current && value !== editorRef.current.innerHTML) {
        editorRef.current.innerHTML = value || '';
        lastValueRef.current = value;
      }
    }
  }, [value]);

  // Gérer les changements
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      lastValueRef.current = html;
      onChange?.(html);
    }
  }, [onChange]);

  // Commandes de formatage
  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  // Insérer un lien
  const insertLink = useCallback(() => {
    const url = prompt('URL du lien:', 'https://');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  return (
    <div className={`simple-editor ${className}`} data-testid="simple-editor">
      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-800 border border-slate-700 rounded-t-lg">
        <button
          type="button"
          onClick={() => execCommand('formatBlock', 'h2')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Titre H2"
        >
          <span className="text-xs font-bold">H2</span>
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', 'h3')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Titre H3"
        >
          <span className="text-xs font-bold">H3</span>
        </button>
        <div className="w-px h-5 bg-slate-600 mx-1" />
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Gras (Ctrl+B)"
        >
          <span className="font-bold text-sm">B</span>
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Italique (Ctrl+I)"
        >
          <span className="italic text-sm">I</span>
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Souligné (Ctrl+U)"
        >
          <span className="underline text-sm">U</span>
        </button>
        <button
          type="button"
          onClick={() => execCommand('strikeThrough')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Barré"
        >
          <span className="line-through text-sm">S</span>
        </button>
        <div className="w-px h-5 bg-slate-600 mx-1" />
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Liste à puces"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Liste numérotée"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h10M7 16h10M4 8h.01M4 12h.01M4 16h.01" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', 'blockquote')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Citation"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </button>
        <div className="w-px h-5 bg-slate-600 mx-1" />
        <button
          type="button"
          onClick={insertLink}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Insérer un lien"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => execCommand('removeFormat')}
          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-sky-400 transition-colors"
          title="Supprimer le formatage"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Zone d'édition */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        className="min-h-[200px] max-h-[400px] overflow-y-auto p-4 bg-slate-950/50 border border-t-0 border-slate-700 rounded-b-lg text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500/50"
        style={{ lineHeight: '1.7' }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      <style>{`
        .simple-editor [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: rgb(100 116 139);
          pointer-events: none;
        }
        .simple-editor [contenteditable] h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: rgb(148 163 184);
        }
        .simple-editor [contenteditable] h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
          color: rgb(148 163 184);
        }
        .simple-editor [contenteditable] p {
          margin-bottom: 1rem;
        }
        .simple-editor [contenteditable] a {
          color: rgb(34 211 238);
          text-decoration: underline;
        }
        .simple-editor [contenteditable] blockquote {
          border-left: 3px solid rgb(34 211 238);
          padding-left: 1rem;
          margin-left: 0;
          margin-right: 0;
          color: rgb(148 163 184);
          font-style: italic;
        }
        .simple-editor [contenteditable] ul,
        .simple-editor [contenteditable] ol {
          padding-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .simple-editor [contenteditable] li {
          margin-bottom: 0.25rem;
        }
        .simple-editor [contenteditable] strong {
          font-weight: 700;
        }
        .simple-editor [contenteditable] em {
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default SimpleEditor;
